import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { match } from "ts-pattern";

import type { CategoryType, PropertySchemaAssociationInput, ValidationError } from "~/common/types";
import {
  GetCategoriesDocument,
  GetCategoryForFormDocument,
  UpsertCategoryDocument,
  type UpsertCategoryMutation,
  type UpsertCategoryMutationVariables,
} from "~/features/admin/categories/gqls";
import type { CategoryUpsertFormData } from "~/features/admin/categories/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseCategoryUpsertOptions {
  onError?: (error: Pick<ValidationError, "fields">) => void;
  onSuccess?: () => void;
}

/**
 * Create or update a category. Mode is inferred from `formData.id`. The caller
 * derives `categoryPath` from the parent + own segment before submitting.
 *
 * V2 ports V1's mutation surface but defers the propertySchemaAssociations
 * editor. On edit, the caller passes the *existing* schemas in
 * `passThroughSchemas` so a basic-fields edit doesn't accidentally clear them.
 * On create, schemas default to `[]`; the server may reject entity-type
 * creates that lack required schemas.
 */
export function useCategoryUpsert({ onError, onSuccess }: UseCategoryUpsertOptions = {}) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [upsertCategory] = useMutation<UpsertCategoryMutation, UpsertCategoryMutationVariables>(
    UpsertCategoryDocument,
    {
      // Apollo cannot trace descendants' effective-schema dependency on this category — evict the whole field.
      update: (cache) => {
        cache.evict({ fieldName: "cnutListEffectiveCategoryPropertySchemas" });
        cache.gc();
      },
    },
  );

  const submit = useCallback(
    async (formData: CategoryUpsertFormData, passThroughSchemas: PropertySchemaAssociationInput[] = []) => {
      setProcessing(true);
      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        const isEdit = Boolean(formData.id);
        const refetchQueries = [
          { query: GetCategoriesDocument, variables: { tenantId } },
          ...(isEdit && formData.id
            ? [{ query: GetCategoryForFormDocument, variables: { tenantId, categoryId: formData.id } }]
            : []),
        ];

        const res = await upsertCategory({
          refetchQueries,
          variables: {
            input: {
              tenantId,
              ...(isEdit && formData.id ? { id: formData.id } : {}),
              name: formData.name,
              description: formData.description,
              categoryPath: { path: formData.categoryPath },
              typeId: formData.typeId as CategoryType,
              relationTypeId: formData.typeId === "relation" ? formData.relationTypeId : null,
              documentUrl: formData.documentUrl,
              i18nLabel: {
                ja: formData.jaLabel ?? undefined,
                en: formData.enLabel ?? undefined,
              },
              categoryPropertySchemas: passThroughSchemas,
            },
          },
        });

        match(res.data?.cnutUpsertCategory)
          .with({ __typename: "ValidationError" }, (e) => onError?.(e))
          .with({ __typename: "CategoryUpsertOutput" }, () => {
            toast.success(
              t(isEdit ? "feature.common.updatedSuccessfully" : "feature.common.createdSuccessfully", {
                model: "category",
              }),
            );
            onSuccess?.();
          })
          .otherwise(() => {});
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [upsertCategory, tenantId, t, onError, onSuccess],
  );

  return { submit, processing };
}
